import { HelpCircle } from 'lucide-react';
import { Tooltip } from './Tooltip';
import type { SalaryStructure } from '../types';

interface SalaryStructureInputsProps {
  mode: 'percentage' | 'value';
  structure: Record<keyof SalaryStructure, string>;
  structureValues: Record<keyof SalaryStructure, string>;
  onStructureChange: (key: keyof SalaryStructure | 'mode', value: string) => void;
  validationError: string | null;
  ctc: string;
}

export function SalaryStructureInputs({
  mode,
  structure,
  structureValues,
  onStructureChange,
  validationError,
  ctc
}: SalaryStructureInputsProps) {
  const components = [
    { key: 'basic', label: 'Basic Salary' },
    { key: 'hra', label: 'HRA' },
    { key: 'da', label: 'DA' },
    { key: 'lta', label: 'LTA' },
    { key: 'specialAllowance', label: 'Special Allowance' },
    { key: 'performanceBonus', label: 'Performance Bonus' }
  ];

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex items-center space-x-4">
        <span className="text-sm text-gray-600">Input Mode:</span>
        <div className="flex space-x-2">
          <button
            onClick={() => onStructureChange('mode', 'percentage')}
            className={`px-3 py-1 text-sm rounded-md ${
              mode === 'percentage'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Percentage
          </button>
          <button
            onClick={() => onStructureChange('mode', 'value')}
            disabled={!ctc}
            className={`px-3 py-1 text-sm rounded-md ${
              mode === 'value'
                ? 'bg-indigo-600 text-white'
                : ctc 
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            Amount
          </button>
        </div>
      </div>

      {!ctc && mode === 'value' ? (
        <div className="text-center p-4 text-gray-500">
          Please enter your CTC first to use value-based input
        </div>
      ) : (
        <div className="space-y-4">
          {components.map(({ key, label }) => (
            <div key={key} className="flex items-center space-x-4 relative">
              <div className="w-1/2 flex items-center space-x-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">{label}</span>
                <div className="inline-block relative">
                  <Tooltip content={`Enter ${mode === 'percentage' ? 'percentage' : 'amount'} for ${label}`}>
                    <div className="relative z-[9999]">
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                    </div>
                  </Tooltip>
                </div>
              </div>
              <div className="w-1/2">
                <input
                  type="text"
                  value={mode === 'percentage' ? structure[key as keyof SalaryStructure] : structureValues[key as keyof SalaryStructure]}
                  onChange={(e) => onStructureChange(key as keyof SalaryStructure, e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder={mode === 'percentage' ? '%' : '₹'}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {validationError && (
        <div className="text-red-500 text-sm mt-2">{validationError}</div>
      )}
    </div>
  );
} 